import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Paper,
  IconButton,
  Chip,
  TextField,
  Button,
  Collapse,
  Divider,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Description as PdfIcon,
  CheckCircle as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Flight as FlightIcon,
  Hotel as HotelIcon,
  DirectionsCar as CarIcon,
  ConfirmationNumber as ActivityIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  Refresh as RetryIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { api } from '../../utils/api';

// ============================================
// TYPES
// ============================================

interface ExtractedFlight {
  airline: string;
  flightNumber: string;
  departure: {
    airport: string;
    city?: string;
    date: string;
    time: string;
  };
  arrival: {
    airport: string;
    city?: string;
    date: string;
    time: string;
  };
  confirmationNumber?: string;
  passengerNames?: string[];
  price?: number;
}

interface ExtractedHotel {
  name: string;
  address?: string;
  city?: string;
  checkIn: string;
  checkOut: string;
  confirmationNumber?: string;
  guestNames?: string[];
  price?: number;
  roomType?: string;
}

interface ExtractedCar {
  company: string;
  pickupLocation: string;
  dropoffLocation: string;
  pickupDate: string;
  dropoffDate: string;
  confirmationNumber?: string;
  driverName?: string;
  price?: number;
  vehicleType?: string;
}

interface ExtractedActivity {
  name: string;
  date: string;
  time?: string;
  location?: string;
  confirmationNumber?: string;
  price?: number;
}

interface ExtractedTripData {
  title: string;
  destination: string;
  startDate: string;
  endDate: string;
  bookingSource: string;
  flights: ExtractedFlight[];
  hotels: ExtractedHotel[];
  cars: ExtractedCar[];
  activities: ExtractedActivity[];
  totalPrice?: number;
  currency?: string;
}

interface PrivacySettings {
  sharePrices: boolean;
  sharePassengerNames: boolean;
  shareConfirmationNumbers: boolean;
}

type ImportStep = 'upload' | 'processing' | 'review' | 'privacy' | 'creating' | 'success' | 'error';

interface TripImportCardProps {
  onImportComplete?: (tripId: string) => void;
  onCancel?: () => void;
}

// ============================================
// COMPONENT
// ============================================

const TripImportCard: React.FC<TripImportCardProps> = ({
  onImportComplete,
  onCancel,
}) => {
  // State
  const [step, setStep] = useState<ImportStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [extractedData, setExtractedData] = useState<ExtractedTripData | null>(null);
  const [editedTitle, setEditedTitle] = useState('');
  const [privacySettings, setPrivacySettings] = useState<PrivacySettings>({
    sharePrices: false,
    sharePassengerNames: false,
    shareConfirmationNumbers: false,
  });
  const [expandedSections, setExpandedSections] = useState({
    flights: true,
    hotels: true,
    cars: false,
    activities: false,
  });

  // Dropzone
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const pdfFile = acceptedFiles.find(f => f.type === 'application/pdf');
    if (pdfFile) {
      setFile(pdfFile);
      setError(null);
    } else {
      setError('Please upload a PDF file');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024, // 10MB
  });

  // ============================================
  // API CALLS
  // ============================================

  const handleUploadAndProcess = async () => {
    if (!file) return;

    setStep('processing');
    setError(null);

    try {
      // Step 1: Get presigned URL for upload
      const uploadUrlResponse = await api.post('/trips/import/upload-url', {
        filename: file.name,
        contentType: 'application/pdf',
      });

      if (!uploadUrlResponse.ok) {
        throw new Error('Failed to get upload URL');
      }

      const { presignedUrl, s3Key } = await uploadUrlResponse.json();

      // Step 2: Upload PDF to S3
      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': 'application/pdf' },
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload PDF');
      }

      // Step 3: Process the PDF with AI extraction (fast path or OCR polling)
      const processResponse = await api.post('/trips/import/process', { s3Key });

      if (!processResponse.ok) {
        const errorData = await processResponse.json();
        throw new Error(errorData.message || errorData.error || 'Failed to process PDF');
      }

      const startData = await processResponse.json();

      let data: any;
      if (startData.status === 'ocr_pending' && startData.jobId) {
        // Image-based PDF: poll the backend until OCR completes.
        const jobId: string = startData.jobId;
        const maxWaitMs = 240000; // 4 min ceiling for multi-page scans
        const pollIntervalMs = 3000;
        const pollStart = Date.now();
        while (Date.now() - pollStart < maxWaitMs) {
          await new Promise(r => setTimeout(r, pollIntervalMs));
          const pollResponse = await api.post('/trips/import/process', { jobId });
          if (!pollResponse.ok) {
            const err = await pollResponse.json();
            throw new Error(err.error || err.message || 'OCR polling failed');
          }
          const pollData = await pollResponse.json();
          if (pollData.status === 'processing') continue;
          if (pollData.status === 'done' && pollData.extractedData) {
            data = pollData.extractedData;
            break;
          }
          throw new Error(pollData.error || pollData.message || 'OCR failed');
        }
        if (!data) {
          throw new Error('OCR timed out. Try a different PDF or add the trip manually.');
        }
      } else if (startData.extractedData) {
        // Text-based PDF: synchronous response with extractedData inline.
        data = startData.extractedData;
      } else {
        throw new Error('Unexpected response from server');
      }

      setExtractedData(data);
      setEditedTitle(data.title);
      setStep('review');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setStep('error');
    }
  };

  const handleCreateTrip = async () => {
    if (!extractedData) return;

    setStep('creating');
    setError(null);

    try {
      const response = await api.post('/trips/import/create', {
        tripData: {
          ...extractedData,
          title: editedTitle,
        },
        privacySettings,
      });

      if (!response.ok) {
        throw new Error('Failed to create trip');
      }

      const { tripId } = await response.json();
      setStep('success');
      
      // Notify parent after brief delay
      setTimeout(() => {
        onImportComplete?.(tripId);
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create trip');
      setStep('error');
    }
  };

  const handleReset = () => {
    setStep('upload');
    setFile(null);
    setError(null);
    setExtractedData(null);
    setEditedTitle('');
    setPrivacySettings({
      sharePrices: false,
      sharePassengerNames: false,
      shareConfirmationNumbers: false,
    });
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // ============================================
  // HELPERS
  // ============================================

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const formatPrice = (price: number | undefined, currency?: string) => {
    if (price === undefined) return null;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(price);
  };

  // ============================================
  // RENDER SECTIONS
  // ============================================

  // Upload Step
  const renderUploadStep = () => (
    <Box>
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : file ? 'success.main' : 'grey.300',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: isDragActive ? 'action.hover' : file ? 'success.lighter' : 'background.paper',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'action.hover',
          },
        }}
      >
        <input {...getInputProps()} />
        {file ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <PdfIcon sx={{ color: 'error.main' }} />
            <Typography variant="body2" fontWeight="medium">
              {file.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ({(file.size / 1024 / 1024).toFixed(2)} MB)
            </Typography>
            <IconButton 
              size="small" 
              onClick={(e) => { e.stopPropagation(); setFile(null); }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <Box>
            <UploadIcon sx={{ fontSize: 40, color: 'grey.400', mb: 1 }} />
            <Typography variant="body2">
              {isDragActive ? 'Drop PDF here' : 'Drag & drop itinerary PDF'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              or click to browse (max 10MB)
            </Typography>
          </Box>
        )}
      </Box>

      {file && (
        <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button variant="outlined" size="small" onClick={() => setFile(null)}>
            Clear
          </Button>
          <Button 
            variant="contained" 
            size="small" 
            onClick={handleUploadAndProcess}
            sx={{ bgcolor: '#2D6E6A', '&:hover': { bgcolor: '#009688' } }}
          >
            Extract Trip Details
          </Button>
        </Box>
      )}
    </Box>
  );

  // Processing Step
  const renderProcessingStep = () => (
    <Box sx={{ py: 3, textAlign: 'center' }}>
      <LinearProgress sx={{ mb: 2, '& .MuiLinearProgress-bar': { bgcolor: '#2D6E6A' } }} />
      <Typography variant="body2" color="text.secondary">
        Extracting trip details from your PDF...
      </Typography>
      <Typography variant="caption" color="text.secondary">
        This may take 10-20 seconds
      </Typography>
    </Box>
  );

  // Review Step
  const renderReviewStep = () => {
    if (!extractedData) return null;

    return (
      <Box>
        {/* Title Editor */}
        <TextField
          fullWidth
          size="small"
          label="Trip Title"
          value={editedTitle}
          onChange={(e) => setEditedTitle(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* Quick Info */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip size="small" label={extractedData.destination} color="primary" />
          <Chip 
            size="small" 
            label={`${formatDate(extractedData.startDate)} - ${formatDate(extractedData.endDate)}`} 
          />
          {extractedData.totalPrice && (
            <Chip 
              size="small"
              label={formatPrice(extractedData.totalPrice, extractedData.currency)} 
              color="secondary" 
            />
          )}
        </Box>

        {/* Booking Source - PRIVATE */}
        <Alert 
          severity="info" 
          icon={<LockIcon fontSize="small" />}
          sx={{ mb: 2, py: 0.5 }}
        >
          <Typography variant="caption">
            <strong>Source:</strong> {extractedData.bookingSource} (visible only to you)
          </Typography>
        </Alert>

        {/* Flights */}
        {extractedData.flights.length > 0 && (
          <Paper variant="outlined" sx={{ mb: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1.5,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
              }}
              onClick={() => toggleSection('flights')}
            >
              <FlightIcon sx={{ mr: 1, color: '#2D6E6A', fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                Flights ({extractedData.flights.length})
              </Typography>
              {expandedSections.flights ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            <Collapse in={expandedSections.flights}>
              <Divider />
              <Box sx={{ p: 1.5 }}>
                {extractedData.flights.map((flight, idx) => (
                  <Box key={idx} sx={{ mb: idx < extractedData.flights.length - 1 ? 1.5 : 0 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {flight.airline} {flight.flightNumber}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {flight.departure.airport} → {flight.arrival.airport}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {formatDate(flight.departure.date)} {flight.departure.time}
                    </Typography>
                    {flight.confirmationNumber && (
                      <Chip 
                        size="small" 
                        label={`Conf: ${flight.confirmationNumber}`} 
                        sx={{ mt: 0.5, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Paper>
        )}

        {/* Hotels */}
        {extractedData.hotels.length > 0 && (
          <Paper variant="outlined" sx={{ mb: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1.5,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
              }}
              onClick={() => toggleSection('hotels')}
            >
              <HotelIcon sx={{ mr: 1, color: '#2D6E6A', fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                Hotels ({extractedData.hotels.length})
              </Typography>
              {expandedSections.hotels ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            <Collapse in={expandedSections.hotels}>
              <Divider />
              <Box sx={{ p: 1.5 }}>
                {extractedData.hotels.map((hotel, idx) => (
                  <Box key={idx} sx={{ mb: idx < extractedData.hotels.length - 1 ? 1.5 : 0 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {hotel.name}
                    </Typography>
                    {hotel.city && (
                      <Typography variant="caption" color="text.secondary">
                        {hotel.city}
                      </Typography>
                    )}
                    <Typography variant="caption" display="block" color="text.secondary">
                      {formatDate(hotel.checkIn)} - {formatDate(hotel.checkOut)}
                    </Typography>
                    {hotel.roomType && (
                      <Typography variant="caption" color="text.secondary">
                        {hotel.roomType}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Paper>
        )}

        {/* Cars */}
        {extractedData.cars.length > 0 && (
          <Paper variant="outlined" sx={{ mb: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1.5,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
              }}
              onClick={() => toggleSection('cars')}
            >
              <CarIcon sx={{ mr: 1, color: '#2D6E6A', fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                Car Rentals ({extractedData.cars.length})
              </Typography>
              {expandedSections.cars ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            <Collapse in={expandedSections.cars}>
              <Divider />
              <Box sx={{ p: 1.5 }}>
                {extractedData.cars.map((car, idx) => (
                  <Box key={idx} sx={{ mb: idx < extractedData.cars.length - 1 ? 1.5 : 0 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {car.company} {car.vehicleType && `- ${car.vehicleType}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {car.pickupLocation} → {car.dropoffLocation}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {formatDate(car.pickupDate)} - {formatDate(car.dropoffDate)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Paper>
        )}

        {/* Activities */}
        {extractedData.activities.length > 0 && (
          <Paper variant="outlined" sx={{ mb: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1.5,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
              }}
              onClick={() => toggleSection('activities')}
            >
              <ActivityIcon sx={{ mr: 1, color: '#2D6E6A', fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                Activities ({extractedData.activities.length})
              </Typography>
              {expandedSections.activities ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            <Collapse in={expandedSections.activities}>
              <Divider />
              <Box sx={{ p: 1.5 }}>
                {extractedData.activities.map((activity, idx) => (
                  <Box key={idx} sx={{ mb: idx < extractedData.activities.length - 1 ? 1.5 : 0 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {activity.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(activity.date)} {activity.time && `at ${activity.time}`}
                    </Typography>
                    {activity.location && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        📍 {activity.location}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Paper>
        )}

        {/* Next button */}
        <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button variant="outlined" size="small" onClick={handleReset}>
            Start Over
          </Button>
          <Button 
            variant="contained" 
            size="small" 
            onClick={() => setStep('privacy')}
            sx={{ bgcolor: '#2D6E6A', '&:hover': { bgcolor: '#009688' } }}
          >
            Set Privacy & Create
          </Button>
        </Box>
      </Box>
    );
  };

  // Privacy Step
  const renderPrivacyStep = () => (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        What can others see when you share this trip?
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        By default, sensitive details are hidden from shared views. You control what's visible.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={privacySettings.sharePrices}
                onChange={(e) => setPrivacySettings(prev => ({ ...prev, sharePrices: e.target.checked }))}
                sx={{ '&.Mui-checked': { color: '#2D6E6A' } }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">Show prices & costs</Typography>
                {privacySettings.sharePrices ? <UnlockIcon fontSize="small" color="action" /> : <LockIcon fontSize="small" color="action" />}
              </Box>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={privacySettings.sharePassengerNames}
                onChange={(e) => setPrivacySettings(prev => ({ ...prev, sharePassengerNames: e.target.checked }))}
                sx={{ '&.Mui-checked': { color: '#2D6E6A' } }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">Show passenger/guest names</Typography>
                {privacySettings.sharePassengerNames ? <UnlockIcon fontSize="small" color="action" /> : <LockIcon fontSize="small" color="action" />}
              </Box>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={privacySettings.shareConfirmationNumbers}
                onChange={(e) => setPrivacySettings(prev => ({ ...prev, shareConfirmationNumbers: e.target.checked }))}
                sx={{ '&.Mui-checked': { color: '#2D6E6A' } }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">Show confirmation numbers</Typography>
                {privacySettings.shareConfirmationNumbers ? <UnlockIcon fontSize="small" color="action" /> : <LockIcon fontSize="small" color="action" />}
              </Box>
            }
          />
        </FormGroup>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockIcon fontSize="small" color="error" />
          <Typography variant="caption" color="text.secondary">
            <strong>Always private:</strong> Booking source, loyalty numbers
          </Typography>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button variant="outlined" size="small" onClick={() => setStep('review')}>
          Back
        </Button>
        <Button 
          variant="contained" 
          size="small" 
          onClick={handleCreateTrip}
          sx={{ bgcolor: '#2D6E6A', '&:hover': { bgcolor: '#009688' } }}
        >
          Create Trip
        </Button>
      </Box>
    </Box>
  );

  // Creating Step
  const renderCreatingStep = () => (
    <Box sx={{ py: 3, textAlign: 'center' }}>
      <LinearProgress sx={{ mb: 2, '& .MuiLinearProgress-bar': { bgcolor: '#2D6E6A' } }} />
      <Typography variant="body2" color="text.secondary">
        Creating your trip...
      </Typography>
    </Box>
  );

  // Success Step
  const renderSuccessStep = () => (
    <Box sx={{ py: 3, textAlign: 'center' }}>
      <CheckIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
      <Typography variant="subtitle1" fontWeight="medium">
        Trip Imported Successfully!
      </Typography>
      <Typography variant="body2" color="text.secondary">
        "{editedTitle}" has been added to your trips
      </Typography>
    </Box>
  );

  // Error Step
  const renderErrorStep = () => (
    <Box sx={{ py: 2 }}>
      <Alert 
        severity="error" 
        action={
          <Button color="inherit" size="small" onClick={handleReset} startIcon={<RetryIcon />}>
            Try Again
          </Button>
        }
      >
        {error || 'Something went wrong. Please try again.'}
      </Alert>
    </Box>
  );

  // ============================================
  // MAIN RENDER
  // ============================================

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2.5,
        borderRadius: 2,
        borderLeft: '5px solid #2D6E6A',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UploadIcon sx={{ color: '#2D6E6A' }} />
          <Typography variant="subtitle1" fontWeight="medium">
            Import Trip from PDF
          </Typography>
        </Box>
        {onCancel && step === 'upload' && (
          <IconButton size="small" onClick={onCancel}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Step indicator */}
      {!['success', 'error'].includes(step) && (
        <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
          {['upload', 'processing', 'review', 'privacy', 'creating'].map((s, idx) => (
            <Box
              key={s}
              sx={{
                flex: 1,
                height: 4,
                borderRadius: 2,
                bgcolor: 
                  ['upload', 'processing', 'review', 'privacy', 'creating'].indexOf(step) >= idx 
                    ? '#2D6E6A' 
                    : 'grey.200',
                transition: 'bgcolor 0.3s',
              }}
            />
          ))}
        </Box>
      )}

      {/* Content */}
      {step === 'upload' && renderUploadStep()}
      {step === 'processing' && renderProcessingStep()}
      {step === 'review' && renderReviewStep()}
      {step === 'privacy' && renderPrivacyStep()}
      {step === 'creating' && renderCreatingStep()}
      {step === 'success' && renderSuccessStep()}
      {step === 'error' && renderErrorStep()}
    </Paper>
  );
};

export default TripImportCard;
